// apiClient.js
import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import {
  AuthenticationApiFactory,
  AuftragApiFactory,
  DokumentApiFactory,
  Configuration,
  LoginCredentialsTypeEnum,
} from "@codefluegel/wowis-rest-typescript-client";
import authStore from "../stores/AuthStore";

// Shared Axios instance for all API calls
const apiAxios = axios.create();

// Always attach the latest token before each request
apiAxios.interceptors.request.use((request) => {
  if (authStore.accessToken) {
    request.headers = request.headers || {};
    request.headers.Authorization = `Bearer ${authStore.accessToken}`;
  }
  return request;
});

// Refresh logic
const refreshAuthLogic = async (failedRequest) => {
  if (!authStore.refreshToken) {
    authStore.handleAuthFailure();
    return Promise.reject(new Error("Missing refresh token"));
  }

  try {
    const config = new Configuration({
      basePath: process.env.REACT_APP_WOWIS_API_URL,
    });

    const { data: refreshResponse } = await AuthenticationApiFactory(
      config,
      process.env.REACT_APP_WOWIS_API_URL,
      axios.create()
    ).getLoginTokens({
      type: LoginCredentialsTypeEnum.Customer,
      grant_type: "refresh_token",
      refresh_token: authStore.refreshToken,
    });

    if (refreshResponse.access_token) {
      authStore.setAccessToken(refreshResponse.access_token);
      if (refreshResponse.refresh_token) authStore.setRefreshToken(refreshResponse.refresh_token);
      authStore.clearAuthFailureReason();
      /* eslint-disable no-param-reassign */
      if (failedRequest.response && failedRequest.response.config) {
        failedRequest.response.config.headers = failedRequest.response.config.headers || {};
        failedRequest.response.config.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
      }
      return Promise.resolve();
    }

    authStore.handleAuthFailure();
    return Promise.reject(new Error("Refresh response did not include an access token"));
  } catch (e) {
    authStore.handleAuthFailure();
    return Promise.reject(e);
  }
};

// Attach refresh interceptor
createAuthRefreshInterceptor(apiAxios, refreshAuthLogic);

// Helper to create configured API instances
function createApi(factory) {
  const config = new Configuration({
    basePath: process.env.REACT_APP_WOWIS_API_URL,
  });
  return factory(config, process.env.REACT_APP_WOWIS_API_URL, apiAxios);
}

// API getters
export const useAuthApi = () => createApi(AuthenticationApiFactory);
export const useAuftragsApi = () => createApi(AuftragApiFactory);
export const useDocumentApi = () => createApi(DokumentApiFactory);
